import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Done as DoneIcon,
  FileDownload as FileDownloadIcon,
  GridOn as GridOnIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ordersApi } from '@/api/orders';
import { tenantApi } from '@/api/tenant';
import { useSubscriptionGate } from '@/hooks/useSubscriptionGate';
import type { Order, OrderItem, OrderStatus } from '@/types/order';
import { downloadExcelFromBackend } from '@/utils/excelExport';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface StatusDef {
  label: string;
  bg: string;
}

const TENANT_TYPE_COLOR: Record<string, string> = {
  RETAILER:    '#C17BAF',
  WHOLESALER:  '#7B61FF',
  DISTRIBUTOR: '#3DD68C',
  OTHERS:      '#9CA3AF',
};

const STATUS_MAP: Record<OrderStatus, StatusDef> = {
  DRAFT:               { label: 'Draft',              bg: '#9ca3af' },
  PLACED:              { label: 'Received',           bg: '#f97316' },
  VIEWED:              { label: 'Viewed',             bg: '#6366f1' },
  CONFIRMED:           { label: 'Confirmed',          bg: '#3b82f6' },
  PARTIALLY_FULFILLED: { label: 'Partially Fulfilled', bg: '#f59e0b' },
  INVOICED:            { label: 'Billed',             bg: '#06b6d4' },
  DISPATCHED:          { label: 'Transferred',        bg: '#8b5cf6' },
  DELIVERED:           { label: 'Completed',          bg: '#22c55e' },
  REJECTED:            { label: 'Rejected',           bg: '#ef4444' },
  CANCELLED:           { label: 'Cancelled',          bg: '#9ca3af' },
};

function formatAmount(n: number) {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDateTime(s?: string) {
  if (!s) return '—';
  return new Date(s).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function downloadCsv(order: Order, displayName: string) {
  const headers = ['#', 'SKU', 'Product Name', 'Qty', 'Free Qty', 'Scheme', 'PTR', 'MRP Rate', 'Discount%', 'GST%', 'Total (PTR)'];
  const rows = (order.items ?? []).map((item: OrderItem, idx: number) => {
    const ptrRate = Number(item.unitPrice) > 0 ? Number(item.unitPrice) : (item.mrp != null ? Number(item.mrp) : 0);
    const ptrTotal = ptrRate * Number(item.qty) - Number(item.discountAmt);
    return [
      idx + 1,
      item.sku,
      `"${item.productName.replace(/"/g, '""')}"`,
      item.qty,
      Number(item.freeQty) > 0 ? item.freeQty : '',
      item.scheme || '',
      Number(item.unitPrice).toFixed(2),
      item.mrp != null ? Number(item.mrp).toFixed(2) : '',
      item.discountPct,
      item.gstRate,
      ptrTotal.toFixed(2),
    ];
  });
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `order-${order.orderNumber || order.id}-${displayName}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadExcel(order: Order, displayName: string) {
  downloadExcelFromBackend({
    title: `Order ${order.orderNumber ?? order.id}`,
    subTitle: `Retailer: ${displayName} | Date: ${new Date().toLocaleDateString('en-IN')}`,
    headers: ['#', 'SKU', 'Product Name', 'Qty', 'Free Qty', 'Scheme', 'PTR', 'MRP Rate', 'Discount%', 'GST%', 'Total (PTR)'],
    rows: (order.items ?? []).map((item: OrderItem, idx: number) => {
      const ptrRate = Number(item.unitPrice) > 0 ? Number(item.unitPrice) : (item.mrp != null ? Number(item.mrp) : 0);
      const ptrTotal = ptrRate * Number(item.qty) - Number(item.discountAmt ?? 0);
      return [
        idx + 1, item.sku, item.productName, item.qty,
        Number(item.freeQty) > 0 ? item.freeQty : '',
        item.scheme || '',
        Number(item.unitPrice).toFixed(2),
        item.mrp != null ? Number(item.mrp).toFixed(2) : '',
        item.discountPct, item.gstRate, ptrTotal.toFixed(2),
      ];
    }),
  });
}

const th: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 11,
  fontWeight: 700,
  color: '#6b7280',
  background: '#f3f4f6',
  borderBottom: '1px solid #e5e7eb',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 12,
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'middle',
};

// ---------------------------------------------------------------------------
// Reject dialog
// ---------------------------------------------------------------------------

function RejectDialog({
  open,
  onClose,
  onConfirm,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading: boolean;
}) {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    if (reason.trim()) onConfirm(reason.trim());
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Reason for Rejection</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          size="small"
          placeholder="e.g. No stock available"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 0.5 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button
          size="small"
          variant="contained"
          color="error"
          disabled={!reason.trim() || loading}
          onClick={handleConfirm}
          sx={{ fontWeight: 700 }}
        >
          Confirm Rejection
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const btnSx = {
  height: 28,
  fontSize: 11,
  fontWeight: 700,
  px: 2,
  minWidth: 96,
  borderRadius: '6px',
  textTransform: 'none' as const,
  letterSpacing: 0.3,
  lineHeight: '28px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
} as const;

export function ReceivedOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [partyName, setPartyName] = useState('');
  const [partyType, setPartyType] = useState('');
  const [partyCity, setPartyCity] = useState('');
  const [partyAddr, setPartyAddr] = useState('');
  const [partyGstin, setPartyGstin] = useState('');
  const [partyDl, setPartyDl] = useState('');
  const [partyPhone, setPartyPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [subOverdueOpen, setSubOverdueOpen] = useState(false);
  const [subDueOpen, setSubDueOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const billInputRef = useRef<HTMLInputElement>(null);
  const subGate = useSubscriptionGate();

  const orderId = Number(id);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Mark as viewed (PLACED → VIEWED) and fetch order
      const viewed = await ordersApi.view(orderId);
      setOrder(viewed);

      // Resolve buyer details from peer directory
      tenantApi.listPeers(undefined, 0, 500).then((res) => {
        const match = res.content.find((p) => p.id === viewed.retailerTenantId);
        if (match) {
          setPartyName(match.name);
          setPartyType(match.tenantType ?? '');
          setPartyCity([match.city, match.state, match.pincode].filter(Boolean).join(', '));
          setPartyAddr(match.address ?? '');
          setPartyGstin(match.gstin ?? '');
          setPartyDl(match.drugLicenseNumber ?? '');
          setPartyPhone(match.phone ?? '');
        }
      }).catch(() => {});
    } catch {
      // view() may fail if not distributor; fall back to plain get
      try {
        const fetched = await ordersApi.get(orderId);
        setOrder(fetched);
      } catch {
        setError('Failed to load order details.');
      }
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void fetchOrder();
  }, [fetchOrder]);

  // Show overdue dialog on page load (direct URL visits); due dialog is shown in list page before navigation
  useEffect(() => {
    if (!subGate.loading && subGate.isBlocked) setSubOverdueOpen(true);
  }, [subGate.loading, subGate.isBlocked]);

  const DUE_REMINDER_KEY = 'b2b_sub_due_last_shown';
  function shouldShowDueReminder() {
    return Date.now() - Number(localStorage.getItem(DUE_REMINDER_KEY) ?? 0) > 3_600_000;
  }
  function markDueReminderShown() {
    localStorage.setItem(DUE_REMINDER_KEY, String(Date.now()));
  }

  const status = order ? STATUS_MAP[order.status] : null;
  const canAccept = order?.status === 'PLACED' || order?.status === 'VIEWED';
  const canReject = order?.status === 'PLACED' || order?.status === 'VIEWED' || order?.status === 'CONFIRMED';
  const canComplete = order?.status === 'CONFIRMED' || order?.status === 'PARTIALLY_FULFILLED' || order?.status === 'DISPATCHED';

  async function doAccept() {
    setActionLoading(true);
    setActionError('');
    try {
      const updated = await ordersApi.confirm(order!.id);
      setOrder(updated);
      setSuccessMsg('Order accepted successfully.');
    } catch {
      setActionError('Failed to accept order.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleAccept() {
    if (!order) return;
    if (subGate.isBlocked) { setSubOverdueOpen(true); return; }
    if (subGate.hasDue && shouldShowDueReminder()) {
      markDueReminderShown();
      pendingActionRef.current = () => void doAccept();
      setSubDueOpen(true);
      return;
    }
    await doAccept();
  }

  async function doReject(reason: string) {
    setRejectOpen(false);
    setActionLoading(true);
    setActionError('');
    try {
      const updated = await ordersApi.reject(order!.id, reason);
      setOrder(updated);
      setSuccessMsg('Order rejected.');
    } catch {
      setActionError('Failed to reject order.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject(reason: string) {
    if (!order) return;
    if (subGate.isBlocked) { setSubOverdueOpen(true); return; }
    if (subGate.hasDue && shouldShowDueReminder()) {
      markDueReminderShown();
      pendingActionRef.current = () => void doReject(reason);
      setSubDueOpen(true);
      return;
    }
    await doReject(reason);
  }

  async function doComplete() {
    setActionLoading(true);
    setActionError('');
    try {
      const updated = await ordersApi.complete(order!.id);
      setOrder(updated);
      setSuccessMsg('Order marked as completed.');
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message;
      setActionError(msg ? `Failed to complete order: ${msg}` : 'Failed to complete order.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleComplete() {
    if (!order) return;
    if (subGate.isBlocked) { setSubOverdueOpen(true); return; }
    if (subGate.hasDue && shouldShowDueReminder()) {
      markDueReminderShown();
      pendingActionRef.current = () => void doComplete();
      setSubDueOpen(true);
      return;
    }
    await doComplete();
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Order not found.'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders/inbox')} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  const displayName = partyName || `Retailer #${order.retailerTenantId}`;
  const repName = order.salesRepName ?? (order.salesRepUserId ? `Rep #${order.salesRepUserId}` : undefined);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Back + title */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/orders/inbox')}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: '#374151',
              border: '1px solid #d1d5db',
              px: 1.5,
              py: 0.4,
              mb: 1,
              '&:hover': { bgcolor: '#f9fafb' },
            }}
          >
            BACK
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', width: '80vw', maxWidth: '80vw' }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111827', lineHeight: 1.2, whiteSpace: 'nowrap', flexShrink: 0 }}>
              Order From: {displayName}
            </Typography>
            {repName && (
              <Box component="span" sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.15, borderRadius: '5px', bgcolor: '#ede9fe', border: '1px solid #c4b5fd', color: '#7c3aed', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                👤 Via Rep: {repName}
              </Box>
            )}
          </Box>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          {canAccept && (
            <Tooltip title="Accept this order">
              <span>
                <Button
                  variant="contained"
                  disabled={actionLoading}
                  startIcon={<CheckCircleIcon sx={{ fontSize: '13px !important' }} />}
                  onClick={handleAccept}
                  sx={{ ...btnSx, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, color: '#fff', boxShadow: '0 1px 3px rgba(22,163,74,0.35)' }}
                >
                  Accept
                </Button>
              </span>
            </Tooltip>
          )}
          {canReject && (
            <Tooltip title="Reject this order">
              <span>
                <Button
                  variant="outlined"
                  disabled={actionLoading}
                  startIcon={<CancelIcon sx={{ fontSize: '13px !important' }} />}
                  onClick={() => setRejectOpen(true)}
                  sx={{ ...btnSx, borderColor: '#ef4444', color: '#ef4444', '&:hover': { bgcolor: '#fef2f2', borderColor: '#dc2626' } }}
                >
                  Reject
                </Button>
              </span>
            </Tooltip>
          )}
          {canComplete && (
            <Tooltip title="Mark order as completed">
              <span>
                <Button
                  variant="contained"
                  disabled={actionLoading}
                  startIcon={
                    actionLoading
                      ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                      : <DoneIcon sx={{ fontSize: '13px !important' }} />
                  }
                  onClick={handleComplete}
                  sx={{ ...btnSx, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, color: '#fff', boxShadow: '0 1px 3px rgba(59,130,246,0.35)' }}
                >
                  Complete Order
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Separator */}
          <Box sx={{ width: '1px', height: 24, bgcolor: '#e5e7eb', mx: 0.25 }} />

          <Tooltip title="Download CSV">
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon sx={{ fontSize: '13px !important' }} />}
              onClick={() => order && downloadCsv(order, displayName)}
              sx={{ ...btnSx, borderColor: '#d1d5db', color: '#374151', '&:hover': { bgcolor: '#f9fafb', borderColor: '#9ca3af' } }}
            >
              CSV
            </Button>
          </Tooltip>
          <Tooltip title="Download Excel">
            <Button
              variant="outlined"
              startIcon={<GridOnIcon sx={{ fontSize: '13px !important' }} />}
              onClick={() => order && downloadExcel(order, displayName)}
              sx={{ ...btnSx, borderColor: '#d1d5db', color: '#374151', '&:hover': { bgcolor: '#f9fafb', borderColor: '#9ca3af' } }}
            >
              Excel
            </Button>
          </Tooltip>
          <Tooltip title="Print order">
            <Button
              variant="outlined"
              startIcon={<PrintIcon sx={{ fontSize: '13px !important' }} />}
              onClick={() => window.print()}
              sx={{ ...btnSx, borderColor: '#d1d5db', color: '#374151', '&:hover': { bgcolor: '#f9fafb', borderColor: '#9ca3af' } }}
            >
              Print Order
            </Button>
          </Tooltip>
          <Tooltip title={order.invoiceFileName ? 'Re-upload invoice document' : 'Upload invoice document'}>
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon sx={{ fontSize: '13px !important' }} />}
              onClick={() => billInputRef.current?.click()}
              sx={{ ...btnSx, borderColor: '#6366f1', color: '#6366f1', '&:hover': { bgcolor: '#f5f3ff', borderColor: '#4f46e5' } }}
            >
              {order.invoiceFileName ? 'Re-upload Invoice' : 'Upload Invoice'}
            </Button>
          </Tooltip>
          <input
            ref={billInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) alert(`Bill upload for "${file.name}" — backend endpoint pending.`);
              e.target.value = '';
            }}
          />
        </Box>
      </Box>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 12 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      {/* ── Summary bar ── */}
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          flexWrap: 'wrap',
          px: 2.5,
          py: 1.5,
          border: '1px solid #e5e7eb',
          borderRadius: 1,
          bgcolor: '#fff',
          mb: 2,
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>ORDER #</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a56db' }}>
            {order.orderNumber || order.id}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>PRODUCTS</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{order.items?.length ?? 0}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>STATUS</Typography>
          <Box
            sx={{
              display: 'inline-block',
              mt: 0.25,
              px: 1,
              py: 0.25,
              borderRadius: 0.5,
              bgcolor: status?.bg ?? '#9ca3af',
              color: '#fff',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.5,
            }}
          >
            {status?.label.toUpperCase()}
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>ORDER TIME</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDateTime(order.orderDate)}</Typography>
        </Box>
        {order.confirmedAt && (
          <Box>
            <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>CONFIRMED TIME</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDateTime(order.confirmedAt)}</Typography>
          </Box>
        )}
        {order.deliveredAt && (
          <Box>
            <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>COMPLETED TIME</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDateTime(order.deliveredAt)}</Typography>
          </Box>
        )}
      </Box>

      {order.cancelReason && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>
          <strong>Rejection Reason:</strong> {order.cancelReason}
        </Alert>
      )}

      {/* ── Buyer Details ── */}
      <Box
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 1,
          bgcolor: '#fff',
          px: 2.5,
          py: 1.5,
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', mb: 1, letterSpacing: 0.5 }}>
          BUYER DETAILS
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* Left: name + location */}
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {displayName}
              {partyType && (
                <Box component="span" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: TENANT_TYPE_COLOR[partyType] ?? TENANT_TYPE_COLOR.OTHERS }}>
                  {partyType}
                </Box>
              )}
            </Typography>
            {partyCity && (
              <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>📍 {partyCity}</Typography>
            )}
            {partyAddr && (
              <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>🏠 {partyAddr}</Typography>
            )}
          </Box>
          {/* Right: compliance + contact */}
          {(partyGstin || partyDl || partyPhone) && (
            <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
              {partyGstin && (
                <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: '#374151' }}>GST:</Box> {partyGstin}
                </Typography>
              )}
              {partyDl && (
                <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: '#374151' }}>DL No:</Box> {partyDl}
                </Typography>
              )}
              {partyPhone && (
                <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>📞 {partyPhone}</Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Product table ── */}
      {(order.items ?? []).some(i => i.approxPrice) && (
        <Box sx={{ mb: 1.5, px: 2, py: 1, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1, fontSize: 12, color: '#92400e' }}>
          ⚠️ Prices marked <strong>~</strong> are approximate (MRP × 80%) — buyer's supplier has not disclosed exact rates. Actuals may vary.
        </Box>
      )}
      <Box
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 780, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['#', 'CODE', 'PRODUCT NAME', 'QTY', 'FREE', 'SCHEME', 'PTR', 'MRP RATE', 'DISCOUNT', 'GST', 'TOTAL (PTR)'].map((h) => (
                  <th
                    key={h}
                    style={{
                      ...th,
                      textAlign: ['#', 'QTY', 'FREE', 'PTR', 'MRP RATE', 'DISCOUNT', 'GST', 'TOTAL (PTR)'].includes(h)
                        ? 'right'
                        : 'left',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(order.items ?? []).map((item, idx) => {
                const mrpRate = item.mrp != null ? Number(item.mrp) : Number(item.unitPrice);
                const ptrRate = Number(item.unitPrice) > 0 ? Number(item.unitPrice) : mrpRate;
                const ptrTotal = ptrRate * Number(item.qty) - Number(item.discountAmt);
                return (
                  <tr key={item.id ?? idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ ...td, textAlign: 'right', color: '#9ca3af', width: 36 }}>{idx + 1}</td>
                    <td style={{ ...td, color: '#1a56db', fontWeight: 600 }}>{item.sku}</td>
                    <td style={{ ...td, minWidth: 200 }}>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{item.productName}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{item.qty}</td>
                    <td style={{ ...td, textAlign: 'right', color: Number(item.freeQty) > 0 ? '#16a34a' : '#9ca3af', fontWeight: 600 }}>
                      {Number(item.freeQty) > 0 ? `+${item.freeQty}` : '—'}
                    </td>
                    <td style={{ ...td, color: '#7B61FF', fontWeight: 600 }}>
                      {item.scheme || '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: item.approxPrice ? '#b45309' : 'inherit' }}>
                      {item.approxPrice ? '~' : ''}₹{Number(item.unitPrice).toFixed(2)}
                      {item.approxPrice && <span style={{ fontSize: 10, marginLeft: 3, color: '#b45309' }}>(Approx.)</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                      ₹{mrpRate.toFixed(2)}
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: '#6b7280' }}>
                      {Number(item.discountPct) > 0 ? `${item.discountPct}%` : '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: '#6b7280' }}>
                      {Number(item.gstRate) > 0 ? `${item.gstRate}%` : '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: item.approxPrice ? '#b45309' : '#152346' }}>
                      {item.approxPrice ? '~' : ''}₹{formatAmount(ptrTotal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#374151' }}>
                  Totals
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                  {(order.items ?? []).reduce((s, i) => s + Number(i.qty), 0).toFixed(0)}
                </td>
                <td colSpan={7} />
              </tr>
            </tfoot>
          </table>
        </Box>
      </Box>

      {/* Amount summary */}
      {(() => {
        const ptrNet = (order.items ?? []).reduce((s, i) => {
          const ptr = Number(i.unitPrice) > 0 ? Number(i.unitPrice) : (i.mrp != null ? Number(i.mrp) : 0);
          return s + ptr * Number(i.qty) - Number(i.discountAmt);
        }, 0);
        const hasApprox = (order.items ?? []).some(i => i.approxPrice);
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Box sx={{ minWidth: 300, border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fff', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2.5, py: 1, borderBottom: '1px solid #f3f4f6' }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Net Amount (PTR){hasApprox ? ' (Approx.)' : ''}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: hasApprox ? '#b45309' : '#111827' }}>{hasApprox ? '~' : ''}₹{formatAmount(ptrNet)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2.5, py: 1.25, bgcolor: '#f8fafc' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346' }}>Bill Total (excl. GST){hasApprox ? ' (Approx.)' : ''}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: hasApprox ? '#b45309' : '#152346' }}>{hasApprox ? '~' : ''}₹{formatAmount(ptrNet)}</Typography>
              </Box>
            </Box>
          </Box>
        );
      })()}

      {/* ── Reject dialog ── */}
      <RejectDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={handleReject}
        loading={actionLoading}
      />

      {/* ── Subscription due reminder dialog ── */}
      <Dialog open={subDueOpen} onClose={() => setSubDueOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#b45309' }}>⚠️ Payment Due</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, mb: 1.5 }}>
            Your subscription payment is due. Please clear the balance to avoid service interruption.
          </Typography>
          {subGate.dueAmount > 0 && (
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#b45309', mb: 1 }}>
              Due: ₹{subGate.dueAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          )}
          <Typography sx={{ fontSize: 12, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1, px: 1.5, py: 1, color: '#92400e' }}>
            Please contact <strong>Ardinotech Ltd</strong> to settle your outstanding invoice.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button size="small" onClick={() => {
            const action = pendingActionRef.current;
            pendingActionRef.current = null;
            setSubDueOpen(false);
            action?.();
          }}>Continue</Button>
          <Button size="small" variant="contained"
            sx={{ bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}
            onClick={() => { pendingActionRef.current = null; setSubDueOpen(false); navigate('/my-subscription'); }}>
            Pay Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Subscription overdue dialog ── */}
      <Dialog open={subOverdueOpen} onClose={() => setSubOverdueOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#dc2626' }}>🔒 Subscription Overdue</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, mb: 1.5 }}>
            Your subscription invoice is overdue by <strong>{subGate.daysSince} day{subGate.daysSince !== 1 ? 's' : ''}</strong>.
            Order actions are blocked.
          </Typography>
          {subGate.dueAmount > 0 && (
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#dc2626', mb: 1 }}>
              Due: ₹{subGate.dueAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          )}
          <Typography sx={{ fontSize: 13, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1, px: 1.5, py: 1, color: '#991b1b' }}>
            Please contact <strong>Ardinotech Ltd</strong> for support or make the payment immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button size="small" onClick={() => setSubOverdueOpen(false)}>Close</Button>
          <Button size="small" variant="contained"
            sx={{ bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}
            onClick={() => { setSubOverdueOpen(false); navigate('/my-subscription'); }}>
            Pay Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Success snackbar ── */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMsg('')} sx={{ fontSize: 13 }}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
